import React, { useState, useCallback, useRef, useEffect } from 'react';
import { GoogleMap, Polygon, Marker } from '@react-google-maps/api';
import { CardFlutuante, CardFlutuanteOpcoes, ContainerModal, OverlayModal, WrapperMapa } from './styled';
import ButtonCustom from '../../../../../../../../../../../../Components/ButtonCustom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFloppyDisk, faXmark, faFileUpload } from '@fortawesome/free-solid-svg-icons';
import { warningAlert } from '../../../../../../../../../../../../utils/alert';
import { Switcher } from '../../../../../../../../../../../../Components/CustomUi/Switcher';
import { TitleH3 } from '../../../../../../../../../../../../Components/CustomUi/titleH3';
import { Render } from '../../../../../../../../../../../../Components/CustomUi/render';
import { AnimatePresence, motion } from 'framer-motion';

// --- Tipagem ---

export interface Coordenada {
  lat: number;
  lng: number;
}

// Nova interface plana conforme solicitado
export interface PointCustom {
  INDICE: number;
  NOME: number | string;
  LATITUDE: number;
  LONGITUDE: number;
  COR?: string | null;
  id?: string | number;
  [key: string]: any;
}

interface PropsModalEditarContorno {
  aberto: boolean;
  contornoInicial?: Coordenada[];
  pontos: PointCustom[] | any; // Atualizado para receber PointCustom
  aoFechar: () => void;
  aoSalvar: (coords: Coordenada[]) => void;
}

// --- Helpers ---

const calcularLimites = (coords: Coordenada[]) => {
  const limites = new google.maps.LatLngBounds();
  coords.forEach((coord) => limites.extend(coord));
  return limites;
};

// --- Componente ---

const OverlayMotion = motion(OverlayModal);
const ContainerMotion = motion(ContainerModal);

export const EditarContorno: React.FC<PropsModalEditarContorno> = ({
  aberto,
  contornoInicial = [],
  pontos,
  aoFechar,
  aoSalvar,
}) => {
  const contornoSeguro = contornoInicial || [];

  // --- Estados ---

  const [historico, setHistorico] = useState<Coordenada[][]>([contornoSeguro]);
  const [indiceHistorico, setIndiceHistorico] = useState(0);

  // Atualizado para PointCustom
  const [points, setPoints] = useState<PointCustom[]>([]);
  const [pontosFora, setPontosFora] = useState<PointCustom[]>([]);

  const [exibir_pontos, setExibir_pontos] = useState<boolean>(false)
  const [contornoValido, setContornoValido] = useState<boolean>(true)

  const refPoligono = useRef<google.maps.Polygon | null>(null);
  const refMapa = useRef<google.maps.Map | null>(null);
  const refInputFile = useRef<HTMLInputElement | null>(null);

  const caminhoAtual = historico[indiceHistorico] || [];

  // --- Lógica de Histórico ---

  const adicionarAoHistorico = (novoCaminho: Coordenada[]) => {
    const novoHistorico = historico.slice(0, indiceHistorico + 1);
    novoHistorico.push(novoCaminho);
    setHistorico(novoHistorico);
    setIndiceHistorico(novoHistorico.length - 1);
  };

  // const desfazer = () => {
  //   if (indiceHistorico > 0) {
  //     setIndiceHistorico(indiceHistorico - 1);
  //   }
  // };

  // const refazer = () => {
  //   if (indiceHistorico < historico.length - 1) {
  //     setIndiceHistorico(indiceHistorico + 1);
  //   }
  // };

  const resetar = () => {
    setHistorico([contornoSeguro]);
    setIndiceHistorico(0);
    if (refMapa.current && contornoSeguro.length > 0) {
      const limites = calcularLimites(contornoSeguro);
      refMapa.current.fitBounds(limites);
    }
  };

  const salvarEdicao = () => {
    aoSalvar(caminhoAtual);
    aoFechar();
  };

  // --- Verificação de Pontos Fora do Polígono (Atualizado para Flat Object) ---

  useEffect(() => {
    if (!window.google || !window.google.maps || !window.google.maps.geometry) return;
    if (caminhoAtual.length < 3 || points.length === 0) {
      setPontosFora([]);
      return;
    }

    const poligonoTemp = new google.maps.Polygon({ paths: caminhoAtual });
    const fora: PointCustom[] = [];

    points.forEach((pt) => {
      // AJUSTE: Leitura direta de LATITUDE e LONGITUDE
      const lat = Number(pt.LATITUDE);
      const lng = Number(pt.LONGITUDE);

      const latLng = new google.maps.LatLng(lat, lng);

      const estaDentro = google.maps.geometry.poly.containsLocation(latLng, poligonoTemp);

      if (!estaDentro) {
        fora.push(pt);
      }
    });

    setPontosFora(fora);

  }, [caminhoAtual, points]);

  // --- Validação de localização do KML importado ---

  const validarContornoComPontos = (novasCoords: Coordenada[]): boolean => {
    if (!window.google || !window.google.maps || !window.google.maps.geometry) return true;
    if (points.length === 0) return true;

    const poligonoTemp = new google.maps.Polygon({ paths: novasCoords });

    // Verifica se pelo menos um ponto da malha está dentro do novo contorno
    const algumDentro = points.some((pt) => {
      const lat = Number(pt.LATITUDE);
      const lng = Number(pt.LONGITUDE);
      const latLng = new google.maps.LatLng(lat, lng);
      return google.maps.geometry.poly.containsLocation(latLng, poligonoTemp);
    });

    return algumDentro;
  };

  // --- Importação de KML ---

  const dispararInputFile = () => {
    refInputFile.current?.click();
  };

  const lerKML = (e: React.ChangeEvent<HTMLInputElement>) => {
    const arquivo = e.target.files?.[0];
    if (!arquivo) return;

    const reader = new FileReader();
    reader.onload = (evento) => {
      const textoKml = evento.target?.result as string;
      try {
        const parser = new DOMParser();
        const xmlDoc = parser.parseFromString(textoKml, "text/xml");

        const coordsTags = xmlDoc.getElementsByTagName("coordinates");

        if (coordsTags.length > 0) {
          const coordsRaw = coordsTags[0].textContent?.trim() || "";

          const novasCoords: Coordenada[] = coordsRaw.split(/\s+/).map((coordStr) => {
            const [lng, lat] = coordStr.split(",").map(Number);
            return { lat, lng };
          }).filter(c => !isNaN(c.lat) && !isNaN(c.lng));

          if (novasCoords.length > 2) {
            // Validar se o KML importado contém os pontos da malha
            const valido = validarContornoComPontos(novasCoords);
            setContornoValido(valido);

            if (!valido) {
              warningAlert('O KML importado não corresponde à localização dos pontos da malha.');
            }

            adicionarAoHistorico(novasCoords);

            if (refMapa.current) {
              const limites = calcularLimites(novasCoords);
              refMapa.current.fitBounds(limites);
            }
          } else {
            alert("KML inválido ou sem coordenadas suficientes.");
          }
        }
      } catch (erro) {
        console.error("Erro ao ler KML", erro);
        alert("Erro ao processar o arquivo KML.");
      }
    };
    reader.readAsText(arquivo);
    e.target.value = "";
  };


  // --- Eventos do Mapa ---

  const aoCarregarMapa = useCallback((map: google.maps.Map) => {
    refMapa.current = map;
    if (contornoSeguro.length > 0) {
      const limites = calcularLimites(contornoSeguro);
      map.fitBounds(limites);
    }
  }, [contornoSeguro]);

  const aoCarregarPoligono = useCallback((poly: google.maps.Polygon) => {
    refPoligono.current = poly;
  }, []);

  const aoEditarPoligono = () => {
    if (refPoligono.current) {
      const path = refPoligono.current.getPath();
      const novasCoords: Coordenada[] = [];

      for (let i = 0; i < path.getLength(); i++) {
        const ponto = path.getAt(i);
        novasCoords.push({ lat: ponto.lat(), lng: ponto.lng() });
      }

      if (JSON.stringify(novasCoords) !== JSON.stringify(caminhoAtual)) {
        adicionarAoHistorico(novasCoords);
      }
    }
  };

  const aoClicarDireito = (e: google.maps.PolyMouseEvent) => {
    if (e.vertex !== undefined && e.vertex !== null) {
      const novoCaminho = [...caminhoAtual];
      novoCaminho.splice(e.vertex, 1);
      adicionarAoHistorico(novoCaminho);
    }
  };

  // --- Inicialização de Pontos (Simplificado para array plano) ---
  useEffect(() => {
    if (pontos) {
      // Se vier como array direto, seta. Se vier dentro de features (legado), extrai.
      if (Array.isArray(pontos)) {
        setPoints(pontos);
      } else if (pontos.features && Array.isArray(pontos.features)) {
        // Caso extremo de compatibilidade, mas idealmente não deve cair aqui com a nova tipagem
        // Se cair, teria que converter, mas vamos assumir array plano
        setPoints(pontos.features);
      }
    }
  }, [pontos]);

  // --- Reset e Init ---
  // --- Reset e Init ---
  useEffect(() => {
    if (aberto) {
      setHistorico([contornoSeguro]);
      setIndiceHistorico(0);
    }
  }, [aberto, contornoSeguro]);

  useEffect(() => {
    if (refMapa.current && caminhoAtual.length > 0) {
      const timer = setTimeout(() => {
        const limites = calcularLimites(caminhoAtual);
        refMapa.current?.fitBounds(limites);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [aberto]);

  return (
    <AnimatePresence>
      {aberto && (
        <OverlayMotion
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
        >
          <ContainerMotion
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <WrapperMapa>
              <GoogleMap
                mapContainerStyle={{ width: '100%', height: '100%' }}
                zoom={14}
                onLoad={aoCarregarMapa}
                options={{
                  mapTypeId: 'satellite',
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                {caminhoAtual && caminhoAtual.length > 0 && (
                  <Polygon
                    onLoad={aoCarregarPoligono}
                    paths={caminhoAtual}
                    options={{
                      fillColor: "#2196F3",
                      fillOpacity: 0.4,
                      strokeColor: "#2196F3",
                      strokeOpacity: 1,
                      strokeWeight: 2,
                      editable: false,
                      draggable: false,
                      zIndex: 1
                    }}
                    onMouseUp={aoEditarPoligono}
                    onDragEnd={aoEditarPoligono}
                    onRightClick={aoClicarDireito}
                  />
                )}

                <Render condition={exibir_pontos}>
                  {points?.map((pt, index) => {
                    // AJUSTE: Leitura direta de props
                    const lng = Number(pt.LONGITUDE);
                    const lat = Number(pt.LATITUDE);

                    // Prioridade para INDICE
                    const pId = pt.INDICE || pt.id || index;

                    // Verifica se está fora usando INDICE ou referência
                    const estaFora = pontosFora.some(pf =>
                      pf.INDICE === pt.INDICE
                    );

                    const corMarcador = estaFora ? '#FF0000' : '#ffffff';

                    const labelText = pt.NOME
                      ? pt.NOME.toString()
                      : (index + 1).toString();

                    const markerKey = `${pId}-${lat}-${lng}`;

                    return (
                      <Marker
                        key={markerKey}
                        position={{ lat, lng }}
                        label={{
                          text: labelText,
                          color: estaFora ? 'white' : '#7b7b7b',
                          fontWeight: 'bold',
                          fontSize: '10px',
                        }}
                        icon={{
                          path: 'M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z',
                          fillColor: corMarcador,
                          fillOpacity: 1,
                          strokeWeight: 1,
                          strokeColor: '#2c2c2c',
                          scale: 1.5,
                          anchor: new google.maps.Point(12, 22),
                          labelOrigin: new google.maps.Point(12, 9),
                        }}
                      />
                    )
                  })}
                </Render>
              </GoogleMap>


              {/* CARD FLUTUANTE */}
              <CardFlutuanteOpcoes>
                <TitleH3>Pontos foras</TitleH3>
                <Switcher label='Exibir pontos' isChecked={exibir_pontos} trigger={setExibir_pontos} />
                <Render condition={exibir_pontos && pontosFora?.length > 0}>
                  <div className='pontos__grid'>
                    {pontosFora?.map((item) => (
                      <div className='pontos__fora'>
                        {/* AJUSTE: Acesso direto ao NOME */}
                        {item.NOME}
                      </div>
                    ))}
                  </div>
                </Render>
              </CardFlutuanteOpcoes>

              <CardFlutuante>

                <input
                  type="file"
                  accept=".kml"
                  ref={refInputFile}
                  style={{ display: 'none' }}
                  onChange={lerKML}
                />
                {/* 
            <ButtonCustom onClick={desfazer} type='secondary' icon={<FontAwesomeIcon icon={faArrowLeftRotate} />} />
            <ButtonCustom onClick={refazer} type='secondary' icon={<FontAwesomeIcon icon={faArrowRightRotate} />} />
            <ButtonCustom onClick={resetar} type='tertiary' icon={<FontAwesomeIcon icon={faArrowsRotate} />} >Resetar</ButtonCustom> */}

                <ButtonCustom
                  onClick={dispararInputFile}
                  type='tertiary'
                  icon={<FontAwesomeIcon icon={faFileUpload} />}
                  title="Importar KML"
                >
                  Importar KML
                </ButtonCustom>

                <div style={{ width: 1, height: 20, background: '#eee' }} />

                <ButtonCustom danger icon={<FontAwesomeIcon icon={faXmark} />} onClick={aoFechar}>
                  Cancelar
                </ButtonCustom>

                <ButtonCustom icon={<FontAwesomeIcon icon={faFloppyDisk} />} onClick={salvarEdicao} disabled={pontosFora?.length > 0 || !contornoValido}>
                  Salvar
                </ButtonCustom>
              </CardFlutuante>

            </WrapperMapa>
          </ContainerMotion>
        </OverlayMotion>
      )}
    </AnimatePresence >
  );
};